import { type CSSProperties, RefObject, useState } from "react";

/**
 * Custom hook to preserve an element's height during state changes.
 * Useful for preventing layout jumps when content is dynamically shown/hidden.
 *
 * @param elementRef - React ref to the element whose height should be preserved
 * @returns Object with preserveHeight function and styles to apply
 */
export const usePreserveElementHeight = <T extends HTMLElement>(
  elementRef: RefObject<T>
) => {
  const [preservedHeight, setPreservedHeight] = useState<string | null>(null);

  const preserveHeight = (preserve: boolean) => {
    if (preserve && elementRef.current) {
      // Capture current height before state change
      const currentHeight = elementRef.current.offsetHeight;
      setPreservedHeight(`${currentHeight}px`);
    } else {
      // Revert to original height
      setPreservedHeight(null);
    }
  };

  const styles: CSSProperties = preservedHeight
    ? { height: preservedHeight, overflowY: "scroll" }
    : {};

  return {
    preserveHeight,
    styles,
  };
};
